import Breadcrumbs, { BreadcrumbsPresentationProps } from "@NA/MobiusOverrides/Breadcrumbs";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import { LinkProps } from "@insite/mobius/Link";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const currentPage = getCurrentPage(state);
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: currentPage.nodeId,
        currentPageType: currentPage.type,
        currentPageName: currentPage.name,
        homePageUrl: getHomePageUrl(state),
        variantName: currentPage.variantName,
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumbs: {
        css: css`
            width: 100%;
            display: flex;
            a {
                &:hover {
                    text-decoration: underline ${getColor("primary")};
                }
                span {
                    color: ${getColor("secondary")};
                    &:first-letter {
                        text-transform: capitalize;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 0 !important;
                            padding-bottom: 0 !important;
                            margin-bottom: -15px;
                            margin-top: -5px;
                        `,
                        css`
                            padding-top: 0 !important;
                            padding-bottom: 0 !important;
                            margin-bottom: -15px;
                            margin-top: -5px;
                        `,
                        null,
                        css`
                            padding-bottom: 10px;
                            overflow-x: auto;
                            white-space: nowrap;
                        `,
                    ],
                    "max",
                )}

            span[aria-current="page"] {
                color: #2e64b0;
                font-weight: 600;
            }

            /* Mobile Breadcrumb for all pages */
            &.mobile-breadcrumb {
                width: calc(100% - 160px);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                                a {
                                    display: none;
                                    &:last-of-type {
                                        display: inline-flex;
                                    }
                                    &:before {
                                        content: "";
                                        background: url("/UserFiles/US/userfiles/Images/Themes/icons/Vector-nxtarrow.png");
                                        background-repeat: no-repeat;
                                        background-position: center left;
                                        width: 7px;
                                        height: 13px;
                                        position: relative;
                                        left: 0px;
                                        top: 33%;
                                        margin-right: 6px;
                                        background-size: 7px !important;
                                        transform: rotate(180deg);
                                    }
                                }

                                > span {
                                    display: none;
                                }
                            `,
                            null,
                            css`
                                padding-bottom: 10px;
                                overflow-x: auto;
                                white-space: nowrap;
                                text-overflow: inherit;
                            `,
                        ],
                        "max",
                    )}

                span[aria-current="page"] {
                    color: ${getColor("text.accent")};
                    font-weight: 500;
                }
            }
        `,
    },
};

type Props = ReturnType<typeof mapStateToProps>;

const PageBreadcrumbs = (props: Props) => {
    let links = props.links || generateLinksFrom(props.linksState, props.nodeId, props.homePageUrl);
    if (links.length > 0) {
        // we need to always direct to the language-specific homepage URL
        const homePageLink = { children: translate("Home"), href: props.homePageUrl };
        const linksWithoutHomePageLink = [...links].splice(1, links.length);
        links = [homePageLink, ...linksWithoutHomePageLink];
    }
    React.useEffect(() => {
        if (typeof window !== "undefined") {
            const selectStateEl: HTMLSelectElement | null = document.querySelector(
                '[data-test-selector="pageBreadcrumbs"]',
            );
            if (selectStateEl) {
                selectStateEl.classList.add("mobile-breadcrumb");
            }
        }
    });

    return (
        <Breadcrumbs
            links={links}
            currentPageType={props.currentPageType}
            currentPageName={props.currentPageName}
            variantName={props.variantName}
            data-test-selector="pageBreadcrumbs"
            {...pageBreadcrumbStyles.breadcrumbs}
        />
    );
};

export function generateLinksFrom(linksState: LinksState, nodeId: string, homePageUrl: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);

    while (currentLink) {
        links.unshift({ children: currentLink.title, href: currentLink.url });
        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
